// client/src/components/creditAssignment/NewCreditAssignmentRequest.js
//
// Form azienda: nuova richiesta di cessione del credito.
// Dropdown cliente (anagrafiche attive) + upload fattura + importo IVA inclusa.
// Validazione client: importo <= plafond rimanente del cliente selezionato.

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PlusCircle } from 'lucide-react';
import FileUploadField from '../utils/FileUploadField';
import {
  formatCurrencyWithSymbol,
  getCurrencySymbol,
  parseCurrencyInput,
  handleCurrencyInputChange
} from '../utils/formatUtils';
import {
  fetchClients,
  createRequest,
  CA_ACCEPT_ATTR,
  CA_ALLOWED_TYPES,
  CA_MAX_SIZE
} from './creditAssignmentApi';

const NewCreditAssignmentRequest = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [amount, setAmount] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchClients()
      .then((list) => setClients(list.filter((c) => c.status === 'active')))
      .catch(() => setError(t('creditAssignment.errors.fetchClients')));
  }, [t]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === clientId) || null,
    [clients, clientId]
  );

  const amountNum = parseCurrencyInput(amount);
  const exceedsPlafond =
    selectedClient && Number.isFinite(amountNum) && amountNum > selectedClient.remainingAmount;

  const onFileSelect = (f) => {
    setError('');
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size > CA_MAX_SIZE) {
      setError(t('creditAssignment.errors.fileTooLarge'));
      return;
    }
    if (f.type && !CA_ALLOWED_TYPES.includes(f.type)) {
      setError(t('creditAssignment.errors.unsupportedType'));
      return;
    }
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!clientId) return setError(t('creditAssignment.errors.selectClient'));
    if (!Number.isFinite(amountNum) || amountNum <= 0)
      return setError(t('creditAssignment.errors.invalidAmount'));
    if (!file) return setError(t('creditAssignment.errors.invoiceRequired'));
    if (exceedsPlafond) return setError(t('creditAssignment.errors.exceedsPlafond'));

    setLoading(true);
    try {
      await createRequest({ clientId, invoiceAmount: amountNum, invoice: file });
      navigate('/company/credit-assignment/requests');
    } catch (err) {
      setError(err.response?.data?.message || t('creditAssignment.errors.createRequest'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="company-dashboard">
      <div className="card">
        <div className="card-title">
          <PlusCircle className="card-title-icon" size={20} />
          <span>{t('creditAssignment.newRequest.title')}</span>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <form className="request-form" onSubmit={onSubmit}>
          <div className="form-group">
            <label htmlFor="ca-client">
              {t('creditAssignment.newRequest.client')} <span className="req">*</span>
            </label>
            <select
              id="ca-client"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              required
            >
              <option value="">{t('creditAssignment.newRequest.selectClient')}</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.clientName}
                </option>
              ))}
            </select>
            {selectedClient && (
              <div className="field-help dark">
                <span>
                  {t('creditAssignment.newRequest.remainingPlafond')}:{' '}
                  {formatCurrencyWithSymbol(selectedClient.remainingAmount, 'EUR')}
                </span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="ca-amount">
              {t('creditAssignment.newRequest.amount')} <span className="req">*</span>
            </label>
            <div className="amount-field">
              <span className="sym">{getCurrencySymbol('EUR')}</span>
              <input
                type="text"
                id="ca-amount"
                inputMode="decimal"
                placeholder={t('forms.enterAmount')}
                value={amount}
                onChange={(e) =>
                  handleCurrencyInputChange(e, (o) => setAmount(o.amount), { amount }, 'amount')
                }
                required
              />
            </div>
            {exceedsPlafond && (
              <div className="field-help" style={{ color: '#dc3545' }}>
                <span>{t('creditAssignment.errors.exceedsPlafond')}</span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="ca-invoice">
              {t('creditAssignment.newRequest.invoice')} <span className="req">*</span>
            </label>
            <FileUploadField
              id="ca-invoice"
              accept={CA_ACCEPT_ATTR}
              file={file}
              onSelect={onFileSelect}
              onClear={() => onFileSelect(null)}
              ctaText={t('creditAssignment.newRequest.invoiceCta')}
              hintText={t('creditAssignment.newRequest.invoiceHelp')}
              changeText={t('creditAssignment.newRequest.invoiceChange')}
              removeText={t('vatCredit.attachments.remove')}
            />
          </div>

          <div className="form-actions" style={{ marginTop: '20px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || exceedsPlafond}
            >
              {loading ? t('common.loading') : t('creditAssignment.newRequest.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewCreditAssignmentRequest;
